import {
  CSSProperties,
  RefObject,
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";

export type AnimVariant = "up" | "down" | "left" | "right" | "scale" | "fade";

/**
 * Props helper for scroll-reveal elements. Spread it onto any Box / Grid item /
 * Stack / Typography (including inside `.map()` — no hooks involved). A single
 * IntersectionObserver in <RevealProvider> flips the `is-visible` class.
 *
 *   <Grid item {...reveal("up", index * 70)}>
 */
export function reveal(
  variant: AnimVariant = "up",
  delay = 0,
  extraClassName = ""
) {
  return {
    className: `anim-reveal${extraClassName ? ` ${extraClassName}` : ""}`,
    "data-anim": variant,
    style: { "--anim-delay": `${delay}ms` } as CSSProperties,
  };
}

/** Live `prefers-reduced-motion` state (subscribes to OS changes). */
export function usePrefersReducedMotion(): boolean {
  const [reduced, setReduced] = useState(false);

  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return;
    const mql = window.matchMedia("(prefers-reduced-motion: reduce)");
    const update = () => setReduced(mql.matches);
    update();
    if (typeof mql.addEventListener === "function") {
      mql.addEventListener("change", update);
      return () => mql.removeEventListener("change", update);
    }
    mql.addListener(update);
    return () => mql.removeListener(update);
  }, []);

  return reduced;
}

type InViewOptions = {
  once?: boolean;
  threshold?: number;
  rootMargin?: string;
};

/** Standalone IntersectionObserver hook. */
export function useInView<T extends Element = HTMLDivElement>(
  options?: InViewOptions
) {
  const once = options?.once ?? true;
  const threshold = options?.threshold ?? 0.15;
  const rootMargin = options?.rootMargin ?? "0px 0px -10% 0px";

  const ref = useRef<T | null>(null);
  const [inView, setInView] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (typeof IntersectionObserver === "undefined") {
      setInView(true);
      return;
    }
    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            setInView(true);
            if (once) observer.disconnect();
          } else if (!once) {
            setInView(false);
          }
        }
      },
      { threshold, rootMargin }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [once, threshold, rootMargin]);

  return { ref, inView } as const;
}

/**
 * Scroll-spy: returns the id of the section nearest the top of the viewport.
 * rAF-throttled and only re-renders on change.
 */
export function useScrollSpy(ids: readonly string[], offset = 140): string {
  const [active, setActive] = useState(ids[0] ?? "");

  useEffect(() => {
    if (typeof window === "undefined") return;

    let frame = 0;
    const compute = () => {
      frame = 0;
      let current = ids[0] ?? "";
      for (const id of ids) {
        const el = document.getElementById(id);
        if (el && el.getBoundingClientRect().top - offset <= 0) {
          current = id;
        }
      }
      setActive((prev) => (prev === current ? prev : current));
    };

    const onScroll = () => {
      if (frame) return;
      frame = window.requestAnimationFrame(compute);
    };

    compute();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);
    return () => {
      if (frame) window.cancelAnimationFrame(frame);
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
    };
  }, [ids, offset]);

  return active;
}

/**
 * Positions a sliding underline under the active item inside `containerRef`.
 * Items must carry the `itemAttribute` (default `data-indicator-item`) whose
 * value matches `activeKey`.
 */
export function useSlidingIndicator<T extends HTMLElement>(
  containerRef: RefObject<T | null>,
  activeKey: string,
  itemAttribute = "data-indicator-item"
): CSSProperties {
  const [style, setStyle] = useState<CSSProperties>({ opacity: 0 });

  const measure = useCallback(() => {
    const container = containerRef.current;
    if (!container) return;
    const item = container.querySelector<HTMLElement>(
      `[${itemAttribute}="${activeKey}"]`
    );
    if (!item) {
      setStyle((prev) =>
        prev.opacity === 0 ? prev : { ...prev, opacity: 0 }
      );
      return;
    }
    const containerRect = container.getBoundingClientRect();
    const itemRect = item.getBoundingClientRect();
    setStyle({
      width: itemRect.width,
      opacity: 1,
      transform: `translateX(${itemRect.left - containerRect.left}px)`,
    });
  }, [containerRef, activeKey, itemAttribute]);

  // Layout effect so the first measurement lands before paint — otherwise the
  // indicator visibly fades in from opacity 0 on every page load.
  useLayoutEffect(() => {
    measure();
  }, [measure]);

  useEffect(() => {
    const container = containerRef.current;
    if (!container || typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver(() => measure());
    observer.observe(container);
    return () => observer.disconnect();
  }, [containerRef, measure]);

  useEffect(() => {
    if (!document.fonts) return;
    let cancelled = false;
    document.fonts.ready
      .then(() => {
        if (!cancelled) measure();
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [measure]);

  return style;
}

/**
 * Pointer-tracked highlight for cards. Writes only `--anim-mx/--anim-my`
 * (used by the light-sweep overlay) straight to the DOM — no rotation, no
 * React re-renders. Disabled for touch pointers and reduced motion.
 */
export function useSpotlight<T extends HTMLElement = HTMLDivElement>() {
  const ref = useRef<T | null>(null);
  const frame = useRef(0);
  const reduce = usePrefersReducedMotion();

  const onPointerMove = useCallback(
    (event: React.PointerEvent<T>) => {
      if (reduce || event.pointerType === "touch") return;
      const el = ref.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      if (!rect.width || !rect.height) return;
      const px = (event.clientX - rect.left) / rect.width;
      const py = (event.clientY - rect.top) / rect.height;
      if (frame.current) cancelAnimationFrame(frame.current);
      frame.current = requestAnimationFrame(() => {
        el.style.setProperty("--anim-mx", `${(px * 100).toFixed(1)}%`);
        el.style.setProperty("--anim-my", `${(py * 100).toFixed(1)}%`);
      });
    },
    [reduce]
  );

  useEffect(
    () => () => {
      if (frame.current) cancelAnimationFrame(frame.current);
    },
    []
  );

  return { ref, onPointerMove } as const;
}
